import React, { useState } from "react";
import axios from "axios";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, CheckCircle2 } from "lucide-react";
import { API } from "../App";

function TaxBuddyLogo({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="tb-rp-bg" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#1d4ed8"/>
          <stop offset="100%" stopColor="#1e1b4b"/>
        </linearGradient>
      </defs>
      <rect width="48" height="48" rx="13" fill="url(#tb-rp-bg)"/>
      <rect x="13" y="8" width="17" height="22" rx="2.5" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.3)" strokeWidth="0.75"/>
      <line x1="17" y1="14" x2="26" y2="14" stroke="rgba(255,255,255,0.7)" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="17" y1="18" x2="26" y2="18" stroke="rgba(255,255,255,0.7)" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="17" y1="22" x2="23" y2="22" stroke="rgba(255,255,255,0.7)" strokeWidth="1.4" strokeLinecap="round"/>
      <circle cx="33" cy="33" r="9" fill="#1d4ed8" stroke="#fff" strokeWidth="1.5"/>
      <text x="33" y="37" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="800" fontFamily="system-ui">$</text>
    </svg>
  );
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setEmailSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, password });
      setResetDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid or expired reset link. Please request a new one.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, #eff6ff 0%, #eef2ff 100%)", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <TaxBuddyLogo size={56} />
          </div>
          <h1 className="text-3xl font-black text-gray-900" style={{ letterSpacing: "-.4px" }}>TaxBuddy</h1>
          <p className="text-gray-500 mt-1 text-sm">Australian Tax Record Organiser</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* ── Forgot password flow (no token) ── */}
          {!token && (
            emailSent ? (
              <div className="text-center space-y-4">
                <CheckCircle2 className="mx-auto text-green-500" size={48} />
                <h2 className="text-xl font-bold text-gray-900">Check your inbox</h2>
                <p className="text-sm text-gray-500">
                  If an account exists for <span className="font-semibold text-gray-700">{email}</span>, we've
                  sent a reset link. It expires in 1 hour.
                </p>
                <Link
                  to="/login"
                  className="block mt-4 text-sm font-semibold text-blue-700 hover:underline"
                >
                  Back to Sign In
                </Link>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Forgot your password?</h2>
                <p className="text-sm text-gray-500 mb-6">
                  Enter your email and we'll send you a reset link.
                </p>
                <form onSubmit={handleForgot} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="you@example.com"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full text-white py-3 rounded-xl font-bold text-sm transition disabled:opacity-60 flex items-center justify-center gap-2"
                    style={{ background: "linear-gradient(135deg, #1d4ed8, #1e1b4b)" }}
                  >
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    Send reset link
                  </button>
                </form>
                <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                  <Link to="/login" className="text-sm font-semibold text-blue-700 hover:underline">
                    Back to Sign In
                  </Link>
                </div>
              </>
            )
          )}

          {/* ── Reset password flow (token present) ── */}
          {token && (
            resetDone ? (
              <div className="text-center space-y-4">
                <CheckCircle2 className="mx-auto text-green-500" size={48} />
                <h2 className="text-xl font-bold text-gray-900">Password updated</h2>
                <p className="text-sm text-gray-500">Your password has been changed. You can now sign in.</p>
                <button
                  onClick={() => navigate("/login")}
                  className="w-full text-white py-3 rounded-xl font-bold text-sm transition mt-2"
                  style={{ background: "linear-gradient(135deg, #1d4ed8, #1e1b4b)" }}
                >
                  Go to Sign In
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Set a new password</h2>
                <p className="text-sm text-gray-500 mb-6">Choose a new password for your TaxBuddy account.</p>
                <form onSubmit={handleReset} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">New Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        placeholder="••••••••"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Minimum 8 characters</p>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full text-white py-3 rounded-xl font-bold text-sm transition disabled:opacity-60 flex items-center justify-center gap-2"
                    style={{ background: "linear-gradient(135deg, #1d4ed8, #1e1b4b)" }}
                  >
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    Update password
                  </button>
                </form>
              </>
            )
          )}
        </div>

        <div className="flex justify-center gap-5 mt-5 text-xs text-gray-400">
          <Link to="/about" className="hover:text-gray-600 transition-colors">About</Link>
          <Link to="/privacy" className="hover:text-gray-600 transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-gray-600 transition-colors">Terms</Link>
        </div>
      </div>
    </div>
  );
}
